import React, { useState, useEffect } from "react";
import { showToast } from "../components/Toast";
import {
  userSettingsAPI,
  calendarConnectionsAPI,
} from "../services/schedulerAPI";
import useAuthStore from "../store/authStore";

const SchedulerSettings = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();

  // General settings state
  const [settings, setSettings] = useState({
    full_name: "",
    username: "",
    language: "en",
    timezone: "Asia/Kolkata",
    time_format: "12_hour",
    date_format: "MM/DD/YYYY",
    week_start: "sunday",
  });

  // Calendar connections state
  const [calendarConnections, setCalendarConnections] = useState([]);

  // Conference integrations state
  const [conferenceSettings, setConferenceSettings] = useState({
    google_meet: { connected: false, is_default: true },
    zoom: { connected: true, is_default: false }, // Zoom is app-level configured
    microsoft_teams: { connected: false, is_default: false },
  });

  const tabs = [
    { id: "general", name: "General" },
    { id: "calendars", name: "Calendars" },
    { id: "integrations", name: "Integrations" },
    { id: "contacts", name: "Contacts" },
  ];

  const timezones = [
    { value: "Asia/Kolkata", label: "Asia/Kolkata (6:37 pm)" },
    { value: "America/Los_Angeles", label: "America/Los Angeles" },
    { value: "America/New_York", label: "America/New York" },
    { value: "Europe/London", label: "Europe/London" },
    { value: "UTC", label: "UTC" },
  ];

  const loadSettings = async () => {
    if (!user?.id) return;

    try {
      const data = await userSettingsAPI.get(user.id);
      setSettings(data);
    } catch (error) {
      if (error.response?.status === 404) {
        // Create default settings
        try {
          const newSettings = await userSettingsAPI.create({
            user_id: user.id,
            full_name: user.name || "User",
            username: user.email?.split("@")[0] || "",
            ...settings,
          });
          setSettings(newSettings);
        } catch (createError) {
          console.error("Failed to create settings:", createError);
        }
      } else {
        console.error("Failed to load settings:", error);
      }
    }
  };

  const loadCalendarConnections = async () => {
    if (!user?.id) return;

    try {
      const data = await calendarConnectionsAPI.list(user.id);
      setCalendarConnections(data);

      // Update conference settings based on connections
      const googleConnected = data.some((c) => c.provider === "google");
      setConferenceSettings((prev) => ({
        ...prev,
        google_meet: { ...prev.google_meet, connected: googleConnected },
      }));
    } catch (error) {
      console.error("Failed to load calendar connections:", error);
    }
  };

  const handleSaveSettings = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      await userSettingsAPI.update(user.id, settings);
      showToast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      showToast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      const response = await calendarConnectionsAPI.getGoogleAuthUrl();
      window.location.href = response.auth_url;
    } catch (error) {
      console.error("Failed to initiate Google Calendar connection:", error);
      showToast.error("Failed to connect Google Calendar");
    }
  };

  const handleReconnectCalendar = async (connectionId) => {
    // Implement reconnection logic
    showToast.info("Reconnecting calendar...");
  };

  useEffect(() => {
    loadSettings();
    loadCalendarConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="bg-white border-b pb-4">
        <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          PERSONAL INFO
        </h3>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {settings.full_name.charAt(0) || "U"}
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{settings.full_name}</h4>
            <p className="text-sm text-gray-600">@{settings.username}</p>
            <button className="text-blue-600 hover:underline text-sm mt-1">
              Edit
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Language
        </label>
        <select
          value={settings.language}
          onChange={(e) =>
            setSettings({ ...settings, language: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="en">English</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <svg
            className="w-4 h-4 inline mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Timezone
        </label>
        <select
          value={settings.timezone}
          onChange={(e) =>
            setSettings({ ...settings, timezone: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {timezones.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Format
        </label>
        <select
          value={settings.time_format}
          onChange={(e) =>
            setSettings({ ...settings, time_format: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="12_hour">12 hour</option>
          <option value="24_hour">24 hour</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Format
        </label>
        <select
          value={settings.date_format}
          onChange={(e) =>
            setSettings({ ...settings, date_format: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Start of Week
        </label>
        <select
          value={settings.week_start}
          onChange={(e) =>
            setSettings({ ...settings, week_start: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="sunday">Sunday</option>
          <option value="monday">Monday</option>
          <option value="saturday">Saturday</option>
        </select>
      </div>

      <button
        onClick={handleSaveSettings}
        disabled={loading}
        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );

  const renderCalendarsTab = () => (
    <div className="space-y-6">
      <div className="bg-white border-b pb-4">
        <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          SYNC
        </h3>

        {calendarConnections.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3 text-blue-800">
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="font-semibold mb-1">Connect Your Calendar</p>
                <p className="text-sm">
                  Connect your Google Calendar to sync your availability, prevent double-bookings,
                  and enable Google Meet integration. Click "Add calendar connection" below to get started.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-4">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          CONNECTIONS
        </h4>

        {calendarConnections.length > 0 ? (
          <div className="space-y-3">
            {calendarConnections.map((connection) => (
              <div
                key={connection.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.5 3h-15A2.5 2.5 0 002 5.5v13A2.5 2.5 0 004.5 21h15a2.5 2.5 0 002.5-2.5v-13A2.5 2.5 0 0019.5 3z" />
                    </svg>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900">
                      Google Calendar
                    </h5>
                    <p className="text-sm text-gray-600">
                      {connection.calendar_name || connection.calendar_id}
                    </p>
                    {!connection.is_primary && (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs mt-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        Connection error
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleReconnectCalendar(connection.id)}
                  className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                >
                  Reconnect
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No calendar connections yet. Connect your Google Calendar to sync
              your availability.
            </p>
          </div>
        )}

        <button
          onClick={handleConnectGoogleCalendar}
          className="w-full mt-4 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-2"
        >
          <span className="text-xl">+</span>
          <span>Add calendar connection</span>
        </button>
      </div>
    </div>
  );

  const handleConnectZoom = async () => {
    showToast.info("Zoom is already configured at the application level and ready to use! Select Zoom when creating booking pages to auto-generate meeting links.");
  };

  const handleConnectTeams = async () => {
    showToast.info("Microsoft Teams OAuth integration coming soon...");
    // TODO: Implement Teams OAuth
    // const authUrl = await teamsAPI.getAuthUrl();
    // window.location.href = authUrl;
  };

  const handleReconnectGoogleMeet = async () => {
    try {
      const response = await calendarConnectionsAPI.getGoogleAuthUrl();
      window.location.href = response.auth_url;
    } catch (error) {
      console.error("Failed to reconnect Google Meet:", error);
      showToast.error("Failed to reconnect. Please try again.");
    }
  };

  const handleSetAsDefault = async (provider) => {
    setConferenceSettings((prev) => ({
      ...prev,
      google_meet: { ...prev.google_meet, is_default: provider === "google_meet" },
      zoom: { ...prev.zoom, is_default: provider === "zoom" },
      microsoft_teams: { ...prev.microsoft_teams, is_default: provider === "microsoft_teams" },
    }));
    showToast.success(`${provider === "google_meet" ? "Google Meet" : provider === "zoom" ? "Zoom" : "Microsoft Teams"} set as default`);
  };

  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      <div className="bg-white border-b pb-4">
        <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          CONFERENCING
        </h3>
        <p className="text-sm text-gray-600">
          Connect a conferencing integration and set the integration as your
          invite location to automatically create conferencing links for new
          events.
        </p>
      </div>

      <div className="space-y-4">
        {/* Zoom */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center text-2xl">
              📹
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h5 className="font-medium text-gray-900">Zoom</h5>
                {conferenceSettings.zoom.is_default && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                    Default
                  </span>
                )}
                {conferenceSettings.zoom.connected && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Connected
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Create Zoom meeting links for new events
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {conferenceSettings.zoom.connected ? (
              <>
                {!conferenceSettings.zoom.is_default && (
                  <button
                    onClick={() => handleSetAsDefault("zoom")}
                    className="px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                  >
                    Set as Default
                  </button>
                )}
                <button
                  onClick={() => showToast.info("Zoom is configured at the application level and cannot be disconnected per user.")}
                  className="px-3 py-2 text-sm text-gray-400 border border-gray-300 rounded-lg cursor-not-allowed"
                  disabled
                >
                  App-Level
                </button>
              </>
            ) : (
              <button
                onClick={handleConnectZoom}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Connect
              </button>
            )}
          </div>
        </div>

        {/* Google Meet */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M22.5 8.25v7.5c0 .83-.67 1.5-1.5 1.5H3c-.83 0-1.5-.67-1.5-1.5v-7.5c0-.83.67-1.5 1.5-1.5h18c.83 0 1.5.67 1.5 1.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h5 className="font-medium text-gray-900">Google Meet</h5>
                {conferenceSettings.google_meet.is_default && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                    Default
                  </span>
                )}
                {conferenceSettings.google_meet.connected && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Connected
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {conferenceSettings.google_meet.connected
                  ? "nikesh.masiwal@gmail.com"
                  : "Connect Google Calendar to use Google Meet"}
              </p>
              {!conferenceSettings.google_meet.connected && (
                <span className="inline-flex items-center gap-1 text-red-600 text-xs mt-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Requires Google Calendar connection
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {conferenceSettings.google_meet.connected ? (
              <>
                {!conferenceSettings.google_meet.is_default && (
                  <button
                    onClick={() => handleSetAsDefault("google_meet")}
                    className="px-3 py-2 text-sm text-green-600 border border-green-600 rounded-lg hover:bg-green-50"
                  >
                    Set as Default
                  </button>
                )}
                <button
                  onClick={handleReconnectGoogleMeet}
                  className="px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                >
                  Reconnect
                </button>
              </>
            ) : (
              <button
                onClick={handleReconnectGoogleMeet}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Connect via Calendar
              </button>
            )}
          </div>
        </div>

        {/* Microsoft Teams */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20.625 8.25h-9.375v9.375h9.375zM3.375 8.25h7.5v7.5h-7.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h5 className="font-medium text-gray-900">Microsoft Teams</h5>
                {conferenceSettings.microsoft_teams.is_default && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                    Default
                  </span>
                )}
                {conferenceSettings.microsoft_teams.connected && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Connected
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Create Teams meeting links for new events. Requires Office 365
                Business account.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {conferenceSettings.microsoft_teams.connected ? (
              <>
                {!conferenceSettings.microsoft_teams.is_default && (
                  <button
                    onClick={() => handleSetAsDefault("microsoft_teams")}
                    className="px-3 py-2 text-sm text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50"
                  >
                    Set as Default
                  </button>
                )}
                <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={handleConnectTeams}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Connect
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-t pt-4">
        <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
          AUTOMATION
        </h3>
        <p className="text-sm text-gray-600">
          No automation integrations available yet. Coming soon: Zapier, Make (Integromat), and custom webhooks.
        </p>
      </div>
    </div>
  );

  const renderContactsTab = () => (
    <div className="text-center py-12">
      <p className="text-gray-500">Contacts management coming soon...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Account Settings
        </h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <div className="flex gap-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === "general" && renderGeneralTab()}
            {activeTab === "calendars" && renderCalendarsTab()}
            {activeTab === "integrations" && renderIntegrationsTab()}
            {activeTab === "contacts" && renderContactsTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulerSettings;
